import { Asset, BoltIcon, Button, Tooltip } from '@clearcover/atlas';
import styles from './styles.module.css';
import * as icons from './assets';
import {
  ChallengeStatus,
  GetChallengesResponseType,
} from 'Rewards/services/endpoints/GetChallenges/types';
import { reportEvent } from 'Shared/analytics';
import { useLazyRewardsApi } from 'Shared/services/Api';
import React, { FC, useEffect, useState } from 'react';
import { ReactComponent as Dots } from './assets/Dots.svg';
import { format } from 'date-fns';
import classNames from 'classnames';

// @ts-ignore Will be used as part of ADVTG-1374
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LoadingSkeleton: FC = () => {
  return (
    <div className={styles.challengesContainer}>
      <div className={styles.challengeCardContainer}>
        <div className={styles.challengeCardTop}>
          <div className={styles.topLoadingContainer}>
            <div>
              <div
                className={styles.topLoadingButton}
                style={{
                  width: 177,
                  height: 20,
                }}
              />
              <div
                className={styles.topLoadingButton}
                style={{
                  width: 177,
                  height: 12,
                  marginTop: 10,
                }}
              />
            </div>
            <div
              className={styles.topLoadingButton}
              style={{
                width: 90,
                height: 20,
              }}
            />
          </div>
        </div>
        <div className={styles.bottomLoadingContainer}>
          <div
            className={styles.bottomLoadingButton}
            style={{
              width: 326,
              height: 12,
            }}
          />
          <div
            className={styles.bottomLoadingButton}
            style={{
              width: 177,
              height: 48,
              marginTop: 19,
            }}
          />
        </div>
      </div>
      <div className={styles.temporaryEmptyCard} />
    </div>
  );
};

const formatChallengeDaysText = (
  status: ChallengeStatus['status'],
  numberOfDays: ChallengeStatus['numberOfDays'],
  endDate: string
) => {
  if (status === 'ACTIVE') {
    return numberOfDays === 1 ? `Last Day` : `${numberOfDays} days left`;
  } else if (status === 'UPCOMING') {
    return numberOfDays === 1
      ? `Starts in 1 day`
      : `Starts in ${numberOfDays} days`;
  } else if (status === 'COMPLETED') {
    return `Ended ${format(new Date(endDate), 'MMMM dd')}`;
  } else {
    return null;
  }
};

const ProgressBar: FC<{
  challenge: GetChallengesResponseType['challenges'][0];
  highestTier: GetChallengesResponseType['challenges'][0]['tiers'][0];
}> = ({ challenge, highestTier }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!highestTier) return;

    setTimeout(
      () =>
        setWidth(
          Math.min(challenge.numberOfPoints / highestTier.threshold, 1) * 100
        ),
      500
    );
  }, [challenge.numberOfPoints, highestTier]);

  return (
    <div className={styles.progressBar}>
      <div className={styles.thresholdZeroTextContainer}>
        <span className={styles.thresholdAmountText}>{0}</span>
      </div>
      {challenge.tiers
        .sort((a, b) => a.threshold - b.threshold)
        .map(tier => {
          // We want to keep the left spacing between 2 and 98 so dots at the beginning and end of the progress bar
          // always appear within it
          const left = Math.max(
            Math.min((tier.threshold / highestTier.threshold) * 100, 98),
            2
          );

          return (
            <Tooltip
              key={tier.name}
              tooltip={`${tier.name} Achievement`}
              position="top"
              style={{
                position: 'absolute',
                textAlign: 'center',
                transform: `translateX(-50%)`,
                left: `${left}%`,
              }}
            >
              <div className={styles.progressTrackerContainer}>
                <div className={styles.progressDotContainer}>
                  <div
                    className={
                      tier.threshold <= challenge.numberOfPoints
                        ? styles.progressDotFilled
                        : styles.progressDot
                    }
                  />
                </div>
                <span className={styles.thresholdAmountText}>
                  {tier.threshold}
                </span>
              </div>
            </Tooltip>
          );
        })}

      <div
        className={styles.progress}
        style={{
          width: challenge.numberOfPoints > 0 ? `calc(${width}% + 6px)` : 0,
        }}
      />
    </div>
  );
};

export const ChallengeCard: FC<{
  challenge: GetChallengesResponseType['challenges'][0];
  rewardsCenterRefresh: () => void;
}> = ({ challenge, rewardsCenterRefresh }) => {
  const highestTier = challenge.tiers.sort(
    (a, b) => b.threshold - a.threshold
  )[0];

  const [
    { loading: isOptInLoading, error: optInError },
    challengeOptIn,
  ] = useLazyRewardsApi({
    url: '/v1/challenges/opt-in',
    method: 'POST',
    data: { challengeId: challenge.challengeId ?? 0 },
  });

  if (challenge.challengeStatus === undefined) {
    return <></>;
  }

  const isChallengeCompleted = challenge.challengeStatus.status === 'COMPLETED';

  return (
    <div className={styles.challengeCardContainer}>
      <div
        className={
          challenge.optedIn || isChallengeCompleted
            ? styles.challengeCardTop
            : styles.challengeCardTopNotOptedIn
        }
      >
        <div className={styles.topLoadingContainer}>
          <div className={styles.challengeTitleContainer}>
            <span className={styles.challengeTitleText}>{challenge.title}</span>
            <span className={styles.challengeSubtitleText}>
              {challenge.subtitle}
            </span>
          </div>
          <Dots
            style={{
              position: 'absolute',
              right: -16,
              top: 16,
              zIndex: 0,
            }}
          />
          <span className={styles.challengeDaysText}>
            {formatChallengeDaysText(
              challenge.challengeStatus.status,
              challenge.challengeStatus.numberOfDays,
              challenge.endDate
            )}
          </span>
          {(challenge.optedIn || isChallengeCompleted) && (
            <div
              className={classNames(styles.progressContainer, {
                [styles.progressContainerCompleted]: isChallengeCompleted,
              })}
            >
              {isChallengeCompleted ? (
                <>
                  <span className={styles.completedPointsText}>
                    {challenge.optedIn ? challenge.numberOfPoints : 0}
                  </span>
                  <span className={styles.completedText}>Completed</span>
                </>
              ) : (
                <>
                  <div className={styles.progressContainerText}>
                    <span className={styles.yourProgressText}>
                      Your progress
                    </span>
                    <span
                      className={styles.progressPointsText}
                    >{`${challenge.numberOfPoints}/${highestTier.threshold} Points`}</span>
                  </div>
                  <ProgressBar
                    challenge={challenge}
                    highestTier={highestTier}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {isChallengeCompleted ? (
        <div className={styles.challengeCompletionMessageContainer}>
          <span className={styles.challengeCompletionMessageText}>
            {challenge.optedIn
              ? challenge.challengeCompletionMessage
              : 'You missed out on this challenge, but there will be more to come. Stay tuned!'}
          </span>
        </div>
      ) : (
        <>
          {challenge.optedIn && (
            <div className={styles.challengeCardBottom}>
              {challenge.tiers
                .sort((a, b) => a.threshold - b.threshold)
                .map(tier => {
                  return (
                    <div key={tier.name} className={styles.tierContainer}>
                      <img
                        src={icons[tier.badgeIcon as keyof typeof icons]}
                        className={styles.badgeImage}
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null; // prevents looping
                          currentTarget.src = icons['Fallback'];
                        }}
                        alt={`${tier.name} badge`}
                      />
                      <div
                        style={{
                          marginLeft: 10,
                          flexDirection: 'column',
                          display: 'flex',
                          alignSelf: 'center',
                        }}
                      >
                        <span className={styles.tierNameText}>{tier.name}</span>
                        <div style={{ flexDirection: 'row', display: 'flex' }}>
                          <div className={styles.tierValueEllipse}>
                            <Asset
                              icon={BoltIcon}
                              height={12}
                              width={12}
                              description="Tier"
                              className={styles.bolt}
                            />
                          </div>
                          <span className={styles.tierThresholdText}>
                            {tier.threshold}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          {!challenge.optedIn && (
            <div className={styles.challengeCardBottomNotOptedIn}>
              <div className={styles.optInTextContainer}>
                <span className={styles.optInTitle}>
                  {challenge.optInTitle}
                </span>
                <span className={styles.optInSubTitle}>
                  {challenge.optInSubtitle}
                </span>
              </div>
              <Button
                onClick={() => {
                  reportEvent('rewards-center:challenge-opt-in-button:click');
                  challengeOptIn().then(() => rewardsCenterRefresh());
                }}
                variant="primary"
                data-testid="opt-in-button"
                isSubmitting={isOptInLoading}
              >
                Opt-In to Challenge
              </Button>
              {optInError && !isOptInLoading && (
                <span className={styles.optInErrorText}>
                  Error processing request. Please try again.
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
